'use client';

import { useState } from 'react';
import { cn, getBackgroundClass } from '@workspace/lib';
import { processHeading } from '@workspace/lib/heading-utils';
import { Button, Icon, Picture, Typography } from '@workspace/ui/common';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { theme } from '@workspace/ui/theme';

import type { VideoModuleProps } from './video-module.types';
import { resolveVideoPoster, resolveVideoUrl } from './video-module.utils';
import { VideoModuleModal } from './VideoModuleModal';

export function VideoModule({ block }: VideoModuleProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (theme !== 'easycraft' || !block || typeof block !== 'object') return null;

  const {
    heading,
    description,
    blockAlignment = 'left',
    videoPosition = false,
    video,
    button,
    backgroundColor = 'bg-secondary-300',
  } = block;
  const { text: headingText } = processHeading(heading);
  const videoUrl = resolveVideoUrl(video);
  const posterUrl = resolveVideoPoster(video);
  const videoIsLeft = videoPosition;
  const textIsCentered = blockAlignment === 'center';
  const textIsRightAligned = blockAlignment === 'right';
  const hasTextContent = Boolean(headingText || description?.length);

  return (
    <div className={cn('w-full', getBackgroundClass(backgroundColor))}>
      <section className="porta-container flex flex-col gap-xl px-md py-xl md:flex-row md:items-stretch md:px-4xl md:py-2xl md:gap-4xl">
        <div
          className={cn(
            'relative order-1 aspect-video w-full overflow-hidden rounded-2xl bg-secondary-200 md:w-[50.75%]',
            videoIsLeft ? 'md:order-1' : 'md:order-2',
          )}
        >
          {posterUrl && (
            <Picture
              src={posterUrl}
              alt={video?.name || headingText || 'Video thumbnail'}
              desktopWidth={720}
              desktopHeight={405}
              mobileWidth={640}
              mobileHeight={360}
              className="block size-full"
              imgClassName="size-full object-cover"
            />
          )}

          {videoUrl && (
            <button
              type="button"
              aria-label={`Play ${video?.name || headingText || 'video'}`}
              onClick={() => setIsModalOpen(true)}
              className="absolute inset-0 flex cursor-pointer items-center justify-center"
            >
              <Icon name="PlayCircleWhite" size={64} className="size-16 md:size-12" />
            </button>
          )}
        </div>

        <div
          className={cn(
            'order-2 flex min-w-0 flex-1 self-stretch flex-col items-start justify-start gap-lg text-left',
            textIsCentered && 'items-center text-center',
            textIsRightAligned && 'items-end text-right',
            videoIsLeft ? 'md:order-2' : 'md:order-1',
          )}
        >
          {hasTextContent && (
            <div className="flex w-full flex-col gap-sm">
              {headingText && (
                <Typography variant="h2" styleVariant="h3" className="font-normal! text-tertiary-500!">
                  {headingText}
                </Typography>
              )}

              {description?.length ? (
                <CustomPortableText value={description} className="w-full max-w-none **:text-tertiary-500!" />
              ) : null}
            </div>
          )}

          {button?.text && button.url && (
            <Button
              variant="primary"
              url={button.url}
              openInNewTab={button.openInNewTab}
              className="bg-primary-300 text-white hover:bg-primary-500"
            >
              {button.text}
            </Button>
          )}
        </div>
      </section>

      {videoUrl && (
        <VideoModuleModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          videoUrl={videoUrl}
          posterUrl={posterUrl}
          videoName={video?.name}
        />
      )}
    </div>
  );
}
